import React, { useContext } from 'react';
import { Field, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import TextAreaField from 'components/TextAreaField';
import validations from 'validations';
import BusinessInfoNavigation from 'router/subrouters/ListingCreationWizard/components/BusinessInfoNavigation';
import ListingCreationSelectField from 'router/subrouters/ListingCreationWizard/components/ListingCreationSelectField';
import { APPOINTMENT_OPTIONS, AppointmentType } from './utils';
import DeadlineArrayField from 'components/DeadlineArrayField';
import TextareaArrayField from 'components/TextareaArrayField';
import ListingSlugField from 'router/subrouters/ListingCreationWizard/components/ListingSlugField';
import {
  DESCRIPTION_MAX_CHARACTERS,
  HIGHLIGHTS_MAX_CHARACTERS,
  KEY_ASSETS_MAX_CHARACTERS,
  PRODUCTS_AND_SERVICES_MAX_CHARACTERS,
  TITLE_MAX_CHARACTERS,
} from 'router/subrouters/ListingCreationWizard/utils';
import { MIN_REQUIRED_LIST_ITEMS } from 'router/subrouters/ListingCreationWizard/data/publicListingRequirements';
import { Listing } from 'models/Listing';
import PatchListingContext from 'router/subrouters/ListingCreationWizard/providers/PatchListing/PatchListing.context';
import ListingContactColleagues from 'router/subrouters/ListingCreationWizard/common-pages/ListingContactColleagues';
import EnquiryRequirementsField from 'components/EnquiryRequirementsField';

import './BusinessInfo.styles.scss';

type BusinessInfoProps = {
  className?: string;
};

const BusinessInfo: React.FC<BusinessInfoProps> = (props) => {
  const { t } = useTranslation();

  const {
    values: { products, appointmentType, keyAssets, highlights },
  } = useFormState<Listing>();

  const { patchListingValue } = useContext(PatchListingContext);

  return (
    <div className="lcw-page">
      <BusinessInfoNavigation />

      <Field
        label={t('appointmentType')}
        name="appointmentType"
        options={APPOINTMENT_OPTIONS}
        component={ListingCreationSelectField}
        validate={validations.required()}
        customOnChange={(appointmentType: AppointmentType) =>
          patchListingValue({ appointmentType })
        }
      />
      {appointmentType === 'Other' && (
        <Field
          name="otherAppointmentType"
          component={InputField}
          placeholder={t('enterOtherAppointmentType')}
          validate={validations.required()}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ otherAppointmentType: e.target.value });
            }
          }}
        />
      )}
      <div className="lcw-custom-label-container">
        <h3>{t('keyListingInformation')}</h3>
      </div>
      <Field
        label={t('listingTitle')}
        name="listingTitle"
        component={InputField}
        validate={validations.required()}
        placeholder={t('enterListingTitle')}
        required
        maxLength={TITLE_MAX_CHARACTERS}
        customOnBlur={(
          e: React.FocusEvent<HTMLInputElement>,
          isValid: boolean,
        ) => {
          if (isValid) {
            patchListingValue({ listingTitle: e.target.value });
          }
        }}
      />
      <ListingSlugField />
      <Field
        label={t('postalCode')}
        placeholder={t('enterPostcode')}
        name="locationPostcode"
        type="number"
        required
        component={InputField}
        validate={validations.composeValidators(
          validations.required(),
          validations.auPostalCode(t('postalCodeInvalid')),
        )}
        customOnBlur={(
          e: React.FocusEvent<HTMLInputElement>,
          isValid: boolean,
        ) => {
          if (isValid) {
            patchListingValue({ locationPostcode: e.target.value });
          }
        }}
      />
      <Field
        label={t('shortDescription')}
        name="shortDescription"
        component={InputField}
        validate={validations.required()}
        placeholder={t('shortDescriptionPlaceholder')}
        maxLength={200}
        required
        customOnBlur={(
          e: React.FocusEvent<HTMLInputElement>,
          isValid: boolean,
        ) => {
          if (isValid) {
            patchListingValue({ shortDescription: e.target.value });
          }
        }}
      />
      <Field
        className="lcw-description-field-textarea"
        label={t('fullDescription')}
        required
        name="businessDescription"
        component={TextAreaField}
        validate={validations.required()}
        placeholder={t('thisWillApearProminently')}
        maxLength={DESCRIPTION_MAX_CHARACTERS}
        customOnBlur={(
          e: React.FocusEvent<HTMLTextAreaElement>,
          isValid: boolean,
        ) => {
          if (isValid) {
            patchListingValue({ businessDescription: e.target.value });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('businessHighlights')}</h3>
      </div>
      <TextareaArrayField
        name="highlights"
        fieldLabel={t('highlight')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={HIGHLIGHTS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_LIST_ITEMS}
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              highlights: values,
              keyAssets,
              products,
            });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('products/Services')}</h3>
      </div>
      <TextareaArrayField
        name="products"
        fieldLabel={t('products/Services')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={PRODUCTS_AND_SERVICES_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_LIST_ITEMS}
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              products: values,
              keyAssets,
              highlights,
            });
          }
        }}
      />
      <div className="lcw-custom-label-container">
        <h3>{t('keyAssets')}</h3>
      </div>
      <TextareaArrayField
        name="keyAssets"
        fieldLabel={t('keyAsset')}
        validate={validations.requireBuisinessInfoArrays()}
        maxFieldLength={KEY_ASSETS_MAX_CHARACTERS}
        required
        minRequired={MIN_REQUIRED_LIST_ITEMS}
        customOnBlur={(values: string[], isValid: boolean) => {
          if (isValid) {
            patchListingValue({
              keyAssets: values,
              products,
              highlights,
            });
          }
        }}
      />

      <DeadlineArrayField />

      <EnquiryRequirementsField />

      <ListingContactColleagues />
    </div>
  );
};

export default BusinessInfo;
